package co.uk.sacursoftware.ssinventory

import androidx.compose.animation.Crossfade
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.ui.*
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.util.AppSession
import co.uk.sacursoftware.ssinventory.util.BackHandler
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole

sealed class Screen {
    object Home : Screen()
    object Settings : Screen()
    object Suppliers : Screen()
    data class SupplierItems(val supplier: Supplier) : Screen()
    object Orders : Screen()
    data class CreateOrder(val supplier: Supplier) : Screen()
    data class OrderDetails(val order: Order) : Screen()
    object Stock : Screen()
    data class StockAndOrders(val mode: StockOrderMode) : Screen()
    object Daily : Screen()
    data class DailyGallery(val area: String, val date: String, val title: String) : Screen()
    object Delivery : Screen()
    data class StockCountForm(val supplier: Supplier, val countId: String? = null) : Screen()
    data class StockHistory(val supplier: Supplier) : Screen()
    data class StockHistoryDetails(val supplier: Supplier, val countId: String, val timestamp: String) : Screen()
    object Reports : Screen()
    object GenerateReport : Screen()
    data class ReportDetails(val report: Report) : Screen()
    object Profile : Screen()
    object Prep : Screen()
    object PrepManagement : Screen()
    object PrepRoutineEditor : Screen()
    object Services : Screen()
    data class AddEditService(val service: Service? = null) : Screen()
    object Tools : Screen()
    data class AddEditTool(val tool: ToolRequest? = null) : Screen()
    object Training : Screen()
    object TrainingCategoryManager : Screen()
    data class AddEditTraining(val trainingItem: TrainingItem? = null) : Screen()
    object Files : Screen()
    object FileFolderManager : Screen()
    data class AddEditFile(val fileItem: FileItem? = null, val folderId: String? = null) : Screen()
    data class ImageViewer(val images: List<String>, val initialIndex: Int = 0, val title: String? = null) : Screen()
}

@Composable
@Preview
fun App(userRole: UserRole = UserRole.ADMIN, onUserRoleChange: (UserRole) -> Unit = {}) {
    CompositionLocalProvider(LocalUserRole provides userRole) {
        SSInventoryTheme {
            var currentScreen by remember { 
                mutableStateOf<Screen>(Screen.Home) 
            }
            var previousScreen by remember { mutableStateOf<Screen>(Screen.Home) }

            BackHandler(enabled = currentScreen != Screen.Home) {
                currentScreen = Screen.Home
            }
            
            var lastSupplierPageIndex by remember { mutableStateOf(AppSession.lastSupplierPageIndex) }
            var lastOrderPageIndex by remember { mutableStateOf(AppSession.lastOrderPageIndex) }
            var lastStockPageIndex by remember { mutableStateOf(AppSession.lastStockPageIndex) }
            var showWebPin by remember { mutableStateOf(false) }

            LaunchedEffect(currentScreen) {
                if (currentScreen !is Screen.ImageViewer) {
                    previousScreen = currentScreen
                }
                AppSession.currentScreen = currentScreen
            }
            LaunchedEffect(lastSupplierPageIndex) {
                AppSession.lastSupplierPageIndex = lastSupplierPageIndex
            }
            LaunchedEffect(lastOrderPageIndex) {
                AppSession.lastOrderPageIndex = lastOrderPageIndex
            }
            LaunchedEffect(lastStockPageIndex) {
                AppSession.lastStockPageIndex = lastStockPageIndex
            }

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .then(
                        if (getPlatform().isWeb) Modifier.padding(vertical = 20.dp)
                        else Modifier
                    )
                    .windowInsetsPadding(
                        if (getPlatform().isWeb) {
                            WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal)
                        } else {
                            WindowInsets.safeDrawing.only(
                                WindowInsetsSides.Horizontal + WindowInsetsSides.Top
                            )
                        }
                    )
            ) {
                    Crossfade(
                        targetState = currentScreen,
                        animationSpec = tween(durationMillis = 400)
                    ) { targetScreen ->
                        when (val screen = targetScreen) {
                            Screen.Home -> HomeScreen(
                                onPrepClick = { currentScreen = Screen.Prep },
                                onStockAndOrdersClick = { currentScreen = Screen.StockAndOrders(StockOrderMode.STOCK) },
                                onReportsClick = { currentScreen = Screen.Reports },
                                onSuppliersClick = { currentScreen = Screen.Suppliers },
                                onDailyClick = { currentScreen = Screen.Daily },
                                onDeliveryClick = { currentScreen = Screen.Delivery },
                                onSettingsClick = if (userRole == UserRole.ADMIN) {{ currentScreen = Screen.Settings }} else null,
                                onServicesClick = { currentScreen = Screen.Services },
                                onToolsClick = { currentScreen = Screen.Tools },
                                onTrainingClick = { currentScreen = Screen.Training },
                                onWebUnlockClick = if (getPlatform().isWeb && userRole != UserRole.ADMIN) {{ showWebPin = true }} else null
                            )
                            Screen.Delivery -> DeliveryScreen(
                                onBack = { currentScreen = Screen.Home },
                                onNavigateToImageViewer = { images, initialIndex, title ->
                                    currentScreen = Screen.ImageViewer(images = images, initialIndex = initialIndex, title = title)
                                }
                            )
                            Screen.Training -> TrainingScreen(
                                onBack = { currentScreen = Screen.Home },
                                onNavigateToAddEdit = { item -> currentScreen = Screen.AddEditTraining(item) },
                                onNavigateToCategoryManager = { currentScreen = Screen.TrainingCategoryManager },
                                onNavigateToImageViewer = { images, initialIndex, title ->
                                    currentScreen = Screen.ImageViewer(images = images, initialIndex = initialIndex, title = title)
                                }
                            )
                            is Screen.ImageViewer -> ImageViewerScreen(
                                images = screen.images,
                                initialIndex = screen.initialIndex,
                                title = screen.title,
                                onBack = { currentScreen = previousScreen }
                            )
                            Screen.TrainingCategoryManager -> TrainingCategoryManagerScreen(
                                onBack = { currentScreen = Screen.Training }
                            )
                            is Screen.AddEditTraining -> AddEditTrainingScreen(
                                trainingItem = screen.trainingItem,
                                onBack = { currentScreen = Screen.Training },
                                onSaved = { currentScreen = Screen.Training }
                            )
                            Screen.Services -> ServicesScreen(
                                onBack = { currentScreen = Screen.Home },
                                onNavigateToAddEdit = { service -> currentScreen = Screen.AddEditService(service) }
                            )
                            is Screen.AddEditService -> AddEditServiceScreen(
                                service = screen.service,
                                onBack = { currentScreen = Screen.Services },
                                onSaved = { currentScreen = Screen.Services }
                            )
                            Screen.Tools -> ToolsScreen(
                                onBack = { currentScreen = Screen.Home },
                                onNavigateToAddEdit = { tool -> currentScreen = Screen.AddEditTool(tool) }
                            )
                            is Screen.AddEditTool -> AddEditToolScreen(
                                tool = screen.tool,
                                onBack = { currentScreen = Screen.Tools },
                                onSaved = { currentScreen = Screen.Tools }
                            )
                            Screen.Prep -> PrepScreen(
                                onBack = { currentScreen = Screen.Home },
                                onNavigateToManagement = { currentScreen = Screen.PrepManagement },
                                onNavigateToRoutines = { currentScreen = Screen.PrepRoutineEditor }
                            )
                            Screen.PrepManagement -> PrepManagementScreen(
                                onBack = { currentScreen = Screen.Prep }
                            )
                            Screen.PrepRoutineEditor -> PrepRoutineEditorScreen(
                                onBack = { currentScreen = Screen.Prep }
                            )
                            is Screen.StockAndOrders -> StockAndOrdersScreen(
                                initialMode = screen.mode,
                                initialPageIndex = lastStockPageIndex,
                                onPageChange = { lastStockPageIndex = it },
                                onBack = { currentScreen = Screen.Home },
                                onNewCount = { currentScreen = Screen.StockCountForm(it) },
                                onAmendCount = { supplier, count -> currentScreen = Screen.StockCountForm(supplier, count.id) },
                                onCreateOrder = { currentScreen = Screen.CreateOrder(it) },
                                onOrderClick = { currentScreen = Screen.OrderDetails(it) },
                                onShowHistory = { currentScreen = Screen.StockHistory(it) }
                            )
                            Screen.Daily -> DailyScreen(
                                onBack = { currentScreen = Screen.Home },
                                onOpenGallery = { area, date, title ->
                                    currentScreen = Screen.DailyGallery(area, date, title)
                                }
                            )
                            is Screen.DailyGallery -> DailyGalleryScreen(
                                area = screen.area,
                                date = screen.date,
                                title = screen.title,
                                onBack = { currentScreen = Screen.Daily },
                                onNavigateToImageViewer = { images, initialIndex, title ->
                                    currentScreen = Screen.ImageViewer(images = images, initialIndex = initialIndex, title = title)
                                }
                            )
                            Screen.Settings -> SettingsScreen(
                                onBack = { currentScreen = Screen.Home },
                                onProfileClick = { currentScreen = Screen.Profile },
                                onFilesClick = { currentScreen = Screen.Files }
                            )
                            Screen.Suppliers -> SupplierScreen(
                                initialPageIndex = lastSupplierPageIndex,
                                onPageChange = { lastSupplierPageIndex = it },
                                onBack = { currentScreen = Screen.Home },
                                onViewItems = { currentScreen = Screen.SupplierItems(it) }
                            )
                            is Screen.SupplierItems -> SupplierItemsScreen(
                                supplierId = screen.supplier.id,
                                supplierName = screen.supplier.name,
                                onBack = { currentScreen = Screen.Suppliers }
                            )
                            Screen.Orders -> StockAndOrdersScreen(
                                initialMode = StockOrderMode.ORDERS,
                                initialPageIndex = lastOrderPageIndex,
                                onPageChange = { lastOrderPageIndex = it },
                                onBack = { currentScreen = Screen.Home },
                                onNewCount = { currentScreen = Screen.StockCountForm(it) },
                                onAmendCount = { supplier, count -> currentScreen = Screen.StockCountForm(supplier, count.id) },
                                onCreateOrder = { currentScreen = Screen.CreateOrder(it) },
                                onOrderClick = { currentScreen = Screen.OrderDetails(it) },
                                onShowHistory = { currentScreen = Screen.StockHistory(it) }
                            )
                            is Screen.CreateOrder -> CreateOrderScreen(
                                supplier = screen.supplier,
                                onBack = { currentScreen = Screen.StockAndOrders(StockOrderMode.ORDERS) },
                                onOrderCreated = { currentScreen = Screen.StockAndOrders(StockOrderMode.ORDERS) }
                            )
                            is Screen.OrderDetails -> OrderDetailsScreen(
                                order = screen.order,
                                onBack = { currentScreen = Screen.StockAndOrders(StockOrderMode.ORDERS) },
                                onOrderUpdated = { currentScreen = Screen.StockAndOrders(StockOrderMode.ORDERS) }
                            )
                            Screen.Stock -> StockAndOrdersScreen(
                                initialMode = StockOrderMode.STOCK,
                                initialPageIndex = lastStockPageIndex,
                                onPageChange = { lastStockPageIndex = it },
                                onBack = { currentScreen = Screen.Home },
                                onNewCount = { currentScreen = Screen.StockCountForm(it) },
                                onAmendCount = { supplier, count -> 
                                    currentScreen = Screen.StockCountForm(supplier, count.id) 
                                },
                                onCreateOrder = { currentScreen = Screen.CreateOrder(it) },
                                onOrderClick = { currentScreen = Screen.OrderDetails(it) },
                                onShowHistory = { currentScreen = Screen.StockHistory(it) }
                            )
                            is Screen.StockCountForm -> StockCountFormScreen(
                                supplier = screen.supplier,
                                existingCountId = screen.countId,
                                onBack = { currentScreen = Screen.StockAndOrders(StockOrderMode.STOCK) },
                                onSaved = { currentScreen = Screen.StockAndOrders(StockOrderMode.STOCK) },
                                onShowHistory = { currentScreen = Screen.StockHistory(screen.supplier) }
                            )
                            is Screen.StockHistory -> StockHistoryScreen(
                                supplierId = screen.supplier.id,
                                onBack = { currentScreen = Screen.StockCountForm(screen.supplier) },
                                onCountClick = { currentScreen = Screen.StockHistoryDetails(screen.supplier, it.id, it.timestamp) }
                            )
                            is Screen.StockHistoryDetails -> StockHistoryDetailsScreen(
                                supplierId = screen.supplier.id,
                                countId = screen.countId,
                                timestamp = screen.timestamp,
                                onBack = { currentScreen = Screen.StockHistory(screen.supplier) }
                            )
                            Screen.Reports -> ReportScreen(
                                onBack = { currentScreen = Screen.Home },
                                onReportClick = { currentScreen = Screen.ReportDetails(it) },
                                onGenerateReport = { currentScreen = Screen.GenerateReport }
                            )
                            Screen.GenerateReport -> GenerateReportScreen(
                                onBack = { currentScreen = Screen.Reports },
                                onReportGenerated = { currentScreen = Screen.Reports }
                            )
                            is Screen.ReportDetails -> ReportDetailsScreen(
                                report = screen.report,
                                onBack = { currentScreen = Screen.Reports }
                            )
                            Screen.Profile -> ProfileScreen(
                                onBack = { currentScreen = Screen.Settings }
                            )
                            Screen.Files -> FilesScreen(
                                onBack = { currentScreen = Screen.Settings },
                                onNavigateToAddEdit = { item, folderId ->
                                    currentScreen = Screen.AddEditFile(item, folderId)
                                },
                                onNavigateToFolderManager = { currentScreen = Screen.FileFolderManager },
                                onNavigateToImageViewer = { images, initialIndex, title ->
                                    currentScreen = Screen.ImageViewer(images = images, initialIndex = initialIndex, title = title)
                                }
                            )
                            Screen.FileFolderManager -> FileFolderManagerScreen(
                                onBack = { currentScreen = Screen.Files }
                            )
                            is Screen.AddEditFile -> AddEditFileScreen(
                                fileItem = screen.fileItem,
                                presetFolderId = screen.folderId,
                                onBack = { currentScreen = Screen.Files },
                                onSaved = { currentScreen = Screen.Files }
                            )
                        }
                    }
                }

            if (showWebPin) {
                WebPinDialog(
                    onRoleSelected = { role ->
                        onUserRoleChange(role)
                        showWebPin = false
                    },
                    onDismiss = { showWebPin = false }
                )
            }
        }
    }
}
